import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Select, MenuItem, Skeleton, Tooltip, Alert, Tabs, Tab, Chip, Button } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import { AppDispatch } from '../../store/store';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { Kpi, cardSx, chartBase } from '../dashboard/SuperAdminDashboard';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  getGroupAdminDashboard,
  getGroupAdminReports,
  type GroupAdminDashboard as Data,
  type GroupAdminReport,
  type ClinicRow,
  type Attention,
} from '../../rtk-apis/groupAdmin/groupAdminApi';
import { TitledBody } from './GroupAdminLayout';
import { thSx, tdSx, ClinicFilter, StatusPill, formatName, formatDate } from './GroupAdminShared';

/* ════════════════════════════════════════════════════════════════════════
   Group Admin dashboard — the clinics of the user's group, side by side.
   Rendered by pages/dashboard for the Group Admin role.

   Same definitions as the Super Admin dashboard (the backend shares them), so
   a clinic's figures match wherever they are read. No billing. Studies come
   from the AE title buckets: a title shared with another clinic cannot be
   split, so those rows say "AE shared" and the group total counts it once.
   ════════════════════════════════════════════════════════════════════════ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SERIES_COLORS = [T.teal, T.sky, T.violet, T.amber, T.green, T.red, '#f472b6', '#facc15'];

const fmt = (n: number | null | undefined, suffix = '') => (n == null ? '—' : `${n.toLocaleString()}${suffix}`);
const deltaText = (d: number | null) => (d == null ? 'no earlier data' : `${d > 0 ? '+' : ''}${d}% vs previous period`);


const Panel: React.FC<{ title: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  subtitle,
  action,
  children,
}) => (
  <Box sx={cardSx}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: T.ink3, mb: '10px' }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

/** "5 h" / "3 d" since `iso`. */
const openFor = (iso?: string | null) => {
  if (!iso) return '—';
  const h = Math.max(0, (Date.now() - new Date(iso).getTime()) / 3600000);
  return h < 48 ? `${Math.round(h)} h` : `${Math.round(h / 24)} d`;
};

// ── Clinic league table ─────────────────────────────────────────────────────
// `better` marks the columns where the best and worst clinic are coloured.
type Column = { label: string; key: string; value: (c: ClinicRow) => number | string | null; better?: 'high' | 'low' };
const COLUMNS: Column[] = [
  { label: 'Clinic', key: 'name', value: (c) => c.name },
  { label: 'Reports', key: 'reports', value: (c) => c.reports },
  { label: 'Change', key: 'reportsDelta', value: (c) => c.reportsDelta },
  { label: 'Echo', key: 'echo', value: (c) => c.echo },
  { label: 'Stress', key: 'stress', value: (c) => c.stress },
  { label: 'Finalized', key: 'finalizedRate', value: (c) => c.finalizedRate, better: 'high' },
  { label: 'Studies', key: 'studies', value: (c) => c.studies },
  { label: 'Unreported', key: 'unreported', value: (c) => c.unreported, better: 'low' },
  { label: 'Avg TAT', key: 'avgTatHours', value: (c) => c.avgTatHours, better: 'low' },
  { label: 'Open', key: 'openReports', value: (c) => c.openReports },
  { label: 'Unassigned', key: 'unassignedReports', value: (c) => c.unassignedReports, better: 'low' },
  { label: 'Overdue', key: 'overdueReports', value: (c) => c.overdueReports, better: 'low' },
  { label: 'Delivered', key: 'delivery', value: (c) => c.distribution?.successRate ?? null, better: 'high' },
];

/** Best and worst value per ranked column, when at least two clinics differ. */
const rankExtremes = (rows: ClinicRow[]) => {
  const out: Record<string, { best: number; worst: number }> = {};
  COLUMNS.filter((col) => col.better).forEach((col) => {
    const values = rows.map(col.value).filter((v): v is number => typeof v === 'number');
    if (values.length < 2) return;
    const hi = Math.max(...values);
    const lo = Math.min(...values);
    if (hi === lo) return;
    out[col.key] = col.better === 'high' ? { best: hi, worst: lo } : { best: lo, worst: hi };
  });
  return out;
};

// ── Needs attention ─────────────────────────────────────────────────────────
const ATTENTION_TABS: { key: Attention; label: string; count: (t: Data['totals']) => number }[] = [
  { key: 'overdue', label: 'Overdue', count: (t) => t.overdueReports },
  { key: 'unassigned', label: 'Unassigned', count: (t) => t.unassignedReports },
  { key: 'open', label: 'All open', count: (t) => t.openReports },
];

const AttentionPanel: React.FC<{ clinicId: string; totals?: Data['totals'] }> = ({ clinicId, totals }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Attention>('overdue');
  const [rows, setRows] = useState<GroupAdminReport[] | null>(null);

  useEffect(() => {
    setRows(null);
    dispatch(getGroupAdminReports({ attention: tab, clinicId: clinicId || undefined, limit: 8 }))
      .unwrap()
      .then((res) => setRows(res.data))
      .catch(() => setRows([]));
  }, [dispatch, tab, clinicId]);

  const viewAll = () => {
    const q = new URLSearchParams({ attention: tab, ...(clinicId ? { clinicId } : {}) });
    navigate(`/group-admin/reports?${q.toString()}`);
  };

  return (
    <Panel
      title="Needs attention"
      subtitle={`Reports not finalized yet, oldest first${totals ? ` · overdue after ${totals.overdueAfterHours} h` : ''}`}
      action={
        <Button size="small" onClick={viewAll} sx={{ textTransform: 'none', color: T.teal, flexShrink: 0 }}>
          View all
        </Button>
      }
    >
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ minHeight: 36, mb: '6px', '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: 13, color: T.ink2 } }}
      >
        {ATTENTION_TABS.map((a) => (
          <Tab key={a.key} value={a.key} label={totals ? `${a.label} (${a.count(totals)})` : a.label} />
        ))}
      </Tabs>
      {!rows && <Skeleton variant="rounded" height={160} />}
      {rows && !rows.length && (
        <Typography sx={{ fontSize: 13, color: T.ink3, py: '18px', textAlign: 'center' }}>Nothing here — all caught up.</Typography>
      )}
      {rows?.map((r) => (
        <Box
          key={r._id}
          onClick={() => navigate(`/group-admin/reports/${r._id}`)}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1.4fr 0.6fr 1.3fr 0.9fr',
            gap: '10px',
            alignItems: 'center',
            py: '8px',
            borderBottom: `1px solid ${T.line}`,
            cursor: 'pointer',
            '&:hover': { background: 'rgba(45,212,191,0.05)' },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>{formatName(r.patientName)}</Typography>
            <Typography sx={{ fontSize: 11.5, color: T.ink3 }}>Study {formatDate(r.studyDate)}</Typography>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: T.ink2 }} noWrap>{r.clinicName || '—'}</Typography>
          <Typography sx={{ fontSize: 12.5, color: T.amber, fontWeight: 600 }}>{openFor(r.createdOn)}</Typography>
          <Typography sx={{ fontSize: 12.5, color: r.reportAssignedTo ? T.ink2 : T.red }} noWrap>
            {r.reportAssignedTo || 'Unassigned'}
          </Typography>
          <Box>
            <StatusPill status={r.status} />
          </Box>
        </Box>
      ))}
    </Panel>
  );
};

const GroupAdminDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [clinicId, setClinicId] = useState('');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState<number | 'all'>(() => new Date().getMonth() + 1);
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: y - 2021 + 1 }, (_, i) => y - i);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await dispatch(getGroupAdminDashboard({ year, month, clinicId: clinicId || undefined })).unwrap());
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Could not load the dashboard');
    } finally {
      setLoading(false);
    }
  }, [dispatch, year, month, clinicId]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals;
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'reports', dir: -1 });
  const clinicRows = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[1];
    return [...(data?.clinics || [])].sort((a, b) => {
      const x = col.value(a);
      const y = col.value(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === 'string' ? x.localeCompare(String(y)) : x - (y as number)) * sort.dir;
    });
  }, [data, sort]);
  const extremes = useMemo(() => rankExtremes(data?.clinics || []), [data]);
  const drilledClinic = clinicId ? data?.clinics.find((c) => c.clinicId === clinicId) : undefined;
  const staffRows = useMemo(
    () => [...(data?.staff || [])].sort((a, b) => a.clinic.localeCompare(b.clinic) || a.role.localeCompare(b.role) || b.reports - a.reports),
    [data]
  );

  const trendOptions = useMemo<Highcharts.Options>(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: 'column', height: 280 },
      xAxis: { ...(chartBase.xAxis as object), categories: data?.trend.categories || MONTHS },
      plotOptions: { column: { borderWidth: 0, borderRadius: 3 } },
      series: (data?.trend.series || []).map((s, i) => ({
        type: s.name === 'Studies' ? 'line' : 'column',
        name: s.name,
        data: s.data,
        color: [T.teal, T.violet, T.sky][i],
      })) as Highcharts.SeriesOptionsType[],
    }),
    [data]
  );

  const clinicTrendOptions = useMemo<Highcharts.Options>(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: 'line', height: 280 },
      xAxis: { ...(chartBase.xAxis as object), categories: data?.clinicTrend.categories || MONTHS },
      series: (data?.clinicTrend.series || []).map((s, i) => ({
        type: 'line',
        name: s.name,
        data: s.data,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
      })) as Highcharts.SeriesOptionsType[],
    }),
    [data]
  );

  const weekdayOptions = useMemo<Highcharts.Options>(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: 'column', height: 220 },
      legend: { enabled: false },
      xAxis: { ...(chartBase.xAxis as object), categories: (data?.weekdayVolume || []).map((d) => d.name) },
      series: [{ type: 'column', name: 'Reports', data: (data?.weekdayVolume || []).map((d) => d.count), color: T.sky, borderWidth: 0 }],
    }),
    [data]
  );

  const filters = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      {clinicId && (
        <Chip
          label={`Viewing ${drilledClinic?.name || 'one clinic'} · show all`}
          onClick={() => setClinicId('')}
          onDelete={() => setClinicId('')}
          sx={{ color: T.teal, border: `1px solid ${T.teal}55`, background: `${T.teal}1a` }}
        />
      )}
      <ClinicFilter value={clinicId} onChange={setClinicId} />
      <Select size="small" value={month} onChange={(e) => setMonth(e.target.value as number | 'all')}>
        <MenuItem value="all">Whole year</MenuItem>
        {MONTHS.map((m, i) => (
          <MenuItem key={m} value={i + 1}>
            {m}
          </MenuItem>
        ))}
      </Select>
      <Select size="small" value={year} onChange={(e) => setYear(Number(e.target.value))}>
        {years.map((y) => (
          <MenuItem key={y} value={y}>
            {y}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  return (
    <TitledBody title="Dashboard" subtitle={data ? `${data.period.label} · ${t?.clinics} clinic${t?.clinics === 1 ? '' : 's'}` : ' '} actions={filters}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: '14px', mb: '16px' }}>
        {loading || !t ? (
          Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} variant="rounded" height={104} />)
        ) : (
          <>
            <Kpi icon={<DescriptionOutlinedIcon fontSize="small" />} label="Reports" value={fmt(t.reports)} sub={deltaText(t.reportsDelta)} />
            <Kpi icon={<MonitorHeartOutlinedIcon fontSize="small" />} label="Echo / Stress" value={`${fmt(t.echo)} / ${fmt(t.stress)}`} sub={`${t.activeClinics} of ${t.clinics} clinics active`} />
            <Kpi icon={<TaskAltOutlinedIcon fontSize="small" />} label="Finalized" value={fmt(t.finalizedRate, '%')} sub={`${fmt(t.finalized)} of ${fmt(t.reports)} reports`} />
            <Kpi icon={<TimerOutlinedIcon fontSize="small" />} label="Avg turnaround" value={fmt(t.avgTatHours, ' h')} sub="Created to finalized" />
            <Kpi icon={<LocalHospitalOutlinedIcon fontSize="small" />} label="Studies received" value={fmt(t.studies)} sub={`${fmt(t.reportedStudies)} reported`} />
            <Kpi icon={<WarningAmberOutlinedIcon fontSize="small" />} label="Unreported studies" value={fmt(t.unreported)} warn={t.unreported > 0} sub="Studies with no report yet" />
            <Kpi
              icon={<PendingActionsOutlinedIcon fontSize="small" />}
              label="Open reports"
              value={fmt(t.openReports)}
              warn={t.overdueReports > 0}
              sub={`${fmt(t.unassignedReports)} unassigned · ${fmt(t.overdueReports)} over ${t.overdueAfterHours} h`}
            />
            <Kpi
              icon={<SendOutlinedIcon fontSize="small" />}
              label="Distribution success"
              value={fmt(data?.distribution.successRate, '%')}
              warn={!!data?.distribution.failed}
              sub={`${fmt(data?.distribution.delivered)} delivered · ${fmt(data?.distribution.failed)} failed`}
            />
          </>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '14px', mb: '16px' }}>
        <Panel title={`Monthly volume · ${year}`} subtitle="Echo and stress reports, studies received">
          {loading ? <Skeleton variant="rounded" height={280} /> : <HighchartsReact highcharts={Highcharts} options={trendOptions} />}
        </Panel>
        <Panel title={`Reports by clinic · ${year}`} subtitle="Reports created per month">
          {loading ? <Skeleton variant="rounded" height={280} /> : <HighchartsReact highcharts={Highcharts} options={clinicTrendOptions} />}
        </Panel>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: '14px', mb: '16px' }}>
        <AttentionPanel clinicId={clinicId} totals={t} />
        <Panel title="Weekday load" subtitle="Reports created by day of week">
          {loading ? <Skeleton variant="rounded" height={220} /> : <HighchartsReact highcharts={Highcharts} options={weekdayOptions} />}
        </Panel>
      </Box>

      <Panel
        title="Clinics"
        subtitle={
          data
            ? `Figures for ${data.period.label}; open work is as of now. Click a clinic to see only its figures; click a heading to sort.`
            : undefined
        }
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <Box
                    component="th"
                    key={col.key}
                    onClick={() => setSort((s) => ({ key: col.key, dir: s.key === col.key ? (-s.dir as 1 | -1) : col.key === 'name' ? 1 : -1 }))}
                    sx={{ ...thSx, cursor: 'pointer', userSelect: 'none', color: sort.key === col.key ? T.ink : T.ink3 }}
                  >
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      {col.label}
                      {sort.key === col.key &&
                        (sort.dir === 1 ? <ArrowUpwardIcon sx={{ fontSize: 13 }} /> : <ArrowDownwardIcon sx={{ fontSize: 13 }} />)}
                    </Box>
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {clinicRows.map((c) => {
                const tone = (key: string, v: number | null) =>
                  v == null || !extremes[key] ? T.ink : v === extremes[key].best ? T.green : v === extremes[key].worst ? T.red : T.ink;
                return (
                  <Box
                    component="tr"
                    key={c.clinicId}
                    onClick={() => setClinicId(c.clinicId === clinicId ? '' : c.clinicId)}
                    sx={{
                      cursor: 'pointer',
                      background: c.clinicId === clinicId ? 'rgba(45,212,191,0.08)' : undefined,
                      '&:hover': { background: 'rgba(45,212,191,0.05)' },
                    }}
                  >
                    <Box component="td" sx={tdSx}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{c.name}</Typography>
                      {c.location && <Typography sx={{ fontSize: 11.5, color: T.ink3 }}>{c.location}</Typography>}
                    </Box>
                    <Box component="td" sx={tdSx}>{fmt(c.reports)}</Box>
                    <Box component="td" sx={{ ...tdSx, color: c.reportsDelta == null ? T.ink3 : c.reportsDelta >= 0 ? T.green : T.red }}>
                      {c.reportsDelta == null ? '—' : `${c.reportsDelta > 0 ? '+' : ''}${c.reportsDelta}%`}
                    </Box>
                    <Box component="td" sx={tdSx}>{fmt(c.echo)}</Box>
                    <Box component="td" sx={tdSx}>{fmt(c.stress)}</Box>
                    <Box component="td" sx={{ ...tdSx, color: tone('finalizedRate', c.finalizedRate) }}>{fmt(c.finalizedRate, '%')}</Box>
                    <Box component="td" sx={tdSx}>
                      {fmt(c.studies)}
                      {c.studiesApprox && (
                        <Tooltip title="This clinic shares its AE title with another clinic, so studies cannot be split between them.">
                          <Box component="span" sx={{ ml: 0.75, fontSize: 10, fontWeight: 700, color: T.amber }}>
                            AE shared
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, color: tone('unreported', c.unreported) }}>{fmt(c.unreported)}</Box>
                    <Box component="td" sx={{ ...tdSx, color: tone('avgTatHours', c.avgTatHours) }}>{fmt(c.avgTatHours, ' h')}</Box>
                    <Box component="td" sx={tdSx}>{fmt(c.openReports)}</Box>
                    <Box component="td" sx={{ ...tdSx, color: tone('unassignedReports', c.unassignedReports) }}>{fmt(c.unassignedReports)}</Box>
                    <Box component="td" sx={{ ...tdSx, color: tone('overdueReports', c.overdueReports) }}>{fmt(c.overdueReports)}</Box>
                    <Box component="td" sx={{ ...tdSx, color: tone('delivery', c.distribution?.successRate ?? null) }}>
                      {fmt(c.distribution?.successRate, '%')}
                      {!!c.distribution?.failed && (
                        <Typography component="span" sx={{ ml: 0.75, fontSize: 11, color: T.red }}>
                          {c.distribution.failed} failed
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </tbody>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: T.ink3, mt: '8px' }}>
          Green is the best clinic in a column, red the worst.
        </Typography>
      </Panel>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: '14px', mt: '16px' }}>
        <Panel title="Staff" subtitle="Reporters (assigned reports) and sonographers (reports started), per clinic">
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Name', 'Role', 'Clinic', 'Reports', 'Finalized', 'Avg TAT'].map((h) => (
                    <Box component="th" key={h} sx={thSx}>
                      {h}
                    </Box>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staffRows.map((p) => (
                  <tr key={`${p.role}-${p.name}-${p.clinicId}`}>
                    <Box component="td" sx={{ ...tdSx, fontWeight: 600 }}>{p.name}</Box>
                    <Box component="td" sx={{ ...tdSx, color: T.ink2 }}>{p.role}</Box>
                    <Box component="td" sx={{ ...tdSx, color: T.ink2 }}>{p.clinic}</Box>
                    <Box component="td" sx={tdSx}>{fmt(p.reports)}</Box>
                    <Box component="td" sx={tdSx}>{p.finalized == null ? '—' : fmt(p.finalized)}</Box>
                    <Box component="td" sx={tdSx}>{fmt(p.avgTatHours, ' h')}</Box>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
          {!loading && !staffRows.length && (
            <Typography sx={{ fontSize: 13, color: T.ink3, py: '14px' }}>No reports in this period.</Typography>
          )}
        </Panel>

        <Panel
          title="Failed sends"
          subtitle={data ? `Latest distributions that did not arrive · ${data.period.label}` : undefined}
        >
          {(data?.failedSends || []).map((f) => (
            <Box
              key={f._id}
              onClick={() => navigate(`/group-admin/reports/${f.reportId}`)}
              sx={{ py: '8px', borderBottom: `1px solid ${T.line}`, cursor: 'pointer', '&:hover': { background: 'rgba(45,212,191,0.05)' } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>{formatName(f.patientName)}</Typography>
                <Typography sx={{ fontSize: 12, color: T.ink3, flexShrink: 0 }}>{formatDate(f.createdAt)}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: T.ink2 }} noWrap>
                {f.clinic} · {f.channel === 'email' ? 'Email' : 'Medical Objects'} → {f.to || '—'}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: T.red }} noWrap>
                {f.status.replace(/_/g, ' ').toLowerCase()}
                {f.error ? ` · ${f.error}` : ''}
              </Typography>
            </Box>
          ))}
          {!loading && !data?.failedSends.length && (
            <Typography sx={{ fontSize: 13, color: T.ink3, py: '14px' }}>No failed sends in this period.</Typography>
          )}
        </Panel>
      </Box>
    </TitledBody>
  );
};

export default GroupAdminDashboard;
